import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Text } from "@performyard/py-components";
import {
	MeetingWorkspaceInsightResponseStreamJson,
	MetaDataMeetingWorkspaceInsightResponseStreamJson,
	UserErrorResponseJson,
} from "@performyard/service-contracts";
import { formatDateAsUTCString } from "@performyard/utils-isomorphic";

import { useAiQuery } from "../../../api/hooks/defs/ai";

import { AISidePanel } from "../SidePanel";

export type MeetingWorkspaceInsightModuleProps = {
	workspaceId: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	filters: Record<string, unknown>;
	isAdmin: boolean;
	allMeetingsCancelled?: boolean;
};

export const MeetingWorkspaceInsightModule = ({
	workspaceId,
	isOpen,
	onOpenChange,
	filters,
	isAdmin,
	allMeetingsCancelled = false,
}: MeetingWorkspaceInsightModuleProps) => {
	const [recreateCount, setRecreateCount] = useState(0);

	const { data, isLoading, isFetching, isRefetching } = useAiQuery<
		MetaDataMeetingWorkspaceInsightResponseStreamJson | UserErrorResponseJson
	>({
		type: "meeting-workspace-insight",
		workspaceId,
		filters,
		findAdditionalContext: false,
		retry: recreateCount > 0 ? "true" : "false",
		responseStyle: "structured",
		recreateCount,
		enabled: isOpen,
	});

	const inProgress = useMemo(() => {
		if (isLoading || isRefetching) return true;
		if (data && typeof data === "object" && "status" in data) {
			return data.status === "in_progress";
		}
		return false;
	}, [data, isLoading, isRefetching]);

	const [cancelledMeetingsError, setCancelledMeetingsError] = useState<UserErrorResponseJson | null>(null);

	// Set cancelled meetings error when allMeetingsCancelled changes
	useEffect(() => {
		if (allMeetingsCancelled) {
			const error: UserErrorResponseJson = {
				message:
					"Insights are unavailable for this meeting workspace. Please ensure that at least one meeting does not have a status of canceled.",
			};
			setCancelledMeetingsError(error);
		} else {
			setCancelledMeetingsError(null);
		}
	}, [allMeetingsCancelled]);

	const isError = (
		response: UserErrorResponseJson | MetaDataMeetingWorkspaceInsightResponseStreamJson | undefined
	): response is UserErrorResponseJson => {
		if (!response || inProgress) return false;

		// Handle cancelled meetings case
		if (allMeetingsCancelled) {
			return true;
		}

		// if response is type MetaDataMeetingWorkspaceInsightResponseStreamJson then return false
		if (response && typeof response === "object" && "status" in response) {
			return false;
		}
		// if response is type UserErrorResponse then return true
		if (
			response &&
			typeof response === "object" &&
			"data" in response &&
			typeof (response as { data?: unknown }).data === "object" &&
			response.data !== null &&
			"message" in (response.data as Record<string, unknown>)
		) {
			response.message = "The generation of Meeting Workspace Insights was unsuccessful. Please try again.";
			return true;
		}
		return false;
	};

	const handleRetry = useCallback(() => {
		setRecreateCount((prev) => prev + 1);
	}, []);

	// Reset recreateCount when panel closes to ensure cached results are fetched on reopen
	useEffect(() => {
		if (!isOpen) {
			setRecreateCount(0);
		}
	}, [isOpen]);

	const renderHeader = (data: MeetingWorkspaceInsightResponseStreamJson) => {
		return (
			<div className="border-radius-1 flex w-full flex-col items-center justify-center gap-4 self-stretch border-b border-solid border-neutral-200 pb-4">
				<div className="flex items-start gap-5 self-stretch rounded bg-neutral-100 p-2">
					<div className="flex flex-1 shrink-0 basis-0 flex-col items-start gap-2">
						<Text size="paragraph-2" weight="semi-bold">
							{data.workspaceName}
						</Text>
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
							Meeting Dates:{" "}
							<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
								{formatDateAsUTCString(new Date(data.startDate))}-
								{formatDateAsUTCString(new Date(data.endDate))}
							</Text>
						</Text>
					</div>
				</div>
			</div>
		);
	};

	const renderInfoSection = () => {
		if (!data || isError(data)) return null;

		const generatedBy = data.createdBy || data.data?.createdBy || null;
		let generatedDate: string | null = null;

		if (data.createdAt) {
			const date = new Date(data.createdAt);
			if (!Number.isNaN(date.getTime())) {
				generatedDate = formatDateAsUTCString(date);
			}
		}

		return (
			<div className="flex flex-col gap-1">
				<Text size="caption" weight="regular" color="neutral-9">
					Insights Populated: {generatedDate}
					{isAdmin && generatedBy ? ` by ${generatedBy}` : ""}
				</Text>
			</div>
		);
	};

	return (
		<AISidePanel<MetaDataMeetingWorkspaceInsightResponseStreamJson>
			type="meeting"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			data={data && !isError(data) ? data : null}
			isLoading={isLoading || isFetching}
			onRetry={handleRetry}
			renderHeader={renderHeader}
			renderInfoSection={renderInfoSection}
			error={isError(data) ? cancelledMeetingsError || data : null}
			logoLoaderProps={{
				textArray: [
					"Compiling Meetings…",
					"Organizing Meetings Content…",
					"Processing Agendas…",
					"Processing Action Items…",
					"Processing Notes…",
					"Generating Insights…",
					"Structuring Insights Format…",
					"Finalizing Results…",
					"Finishing Up…",
				],
				mainText: "Summarizing Meetings",
			}}
			sectionTitleMap={{
				executiveSummary: "Executive Summary",
				progressAndGrowth: "Progress & Growth",
				keyAccomplishmentsAndWins: "Key Accomplishments & Wins",
				developmentAreasAndGrowthOpportunities: "Development Areas & Growth Opportunities",
				notableDiscussionsAndDecisions: "Notable Discussions & Decisions",
				actionItemAnalysis: "Action Item Analysis",
			}}
		/>
	);
};
